<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query";
import { twMerge } from "tailwind-merge";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { toast } from "vue-sonner";

import { postClaimFreeText } from "@/api";
import { useUser } from "@/composables/api/useUser";

import Button from "@/components/ui/Button.vue";

const props = defineProps<{
  receiptStatus?: string;
}>();

const emit = defineEmits<{
  (e: "sent"): void;
  (e: "close"): void;
}>();

const { data: user } = useUser();

const model = defineModel<string>({ required: true });

const { t } = useI18n();
const route = useRoute();
const queryClient = useQueryClient();

const isLoading = ref(false);

async function handleSubmit() {
  const receiptId = route.params.id;

  if (isLoading.value || typeof receiptId !== "string") {
    return;
  }

  isLoading.value = true;

  try {
    await postClaimFreeText(receiptId, model.value);
    queryClient.invalidateQueries({ queryKey: ["receipt", receiptId] });
    toast(t("toast.claimFreeTextSuccess"), { position: "top-center" });
    // Only emit 'sent' for non-completed receipts to trigger ConfirmClaimDialog
    if (props.receiptStatus !== "completed" && props.receiptStatus !== "rejected") {
      emit("sent");
    }
    emit("close");
  } catch (error) {
    console.error(error);
  }

  isLoading.value = false;
}
</script>

<template>
  <div class="flex w-full flex-col gap-12">
    <div class="flex flex-col gap-5">
      <h1
        :class="
          twMerge(
            'text-galbani text-2xl font-semibold',
            user?.current_mechanic === 'lactel' && 'text-lactel-blue',
          )
        "
      >
        {{ $t("dialog.otherClaim.title") }}
      </h1>

      <p class="text-secondary text-base font-medium">
        {{ $t("dialog.otherClaim.description") }}
      </p>

      <textarea
        v-model="model"
        :class="
          twMerge(
            'border-galbani text-secondary min-h-52 w-full rounded-sm border px-4 py-5',
            user?.current_mechanic === 'lactel' && 'border-lactel-blue',
          )
        "
        :placeholder="$t('dialog.otherClaim.placeholder')"
      />
    </div>

    <Button variant="secondary" :disabled="!model.length" @click="handleSubmit">
      {{ $t("dialog.otherClaim.button") }}
    </Button>
  </div>
</template>
